import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, X, Coffee, Upload, Layers } from 'lucide-react';
import MuggPhotos, { splitViewName, viewRank } from '@/components/MuggPhotos';

function filenameToTitle(filename) {
  return filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const PRODUCT_TYPES = [
  { id: 'mug', label: '15oz Ceramic Coffee Mugg', price: 18 },
  { id: 'tumbler', label: '20oz Stainless Steel Tumbler', price: 28 },
  { id: 'sippy', label: '12oz Sippy Cup', price: 20 },
  { id: 'coaster', label: '4x4 Ceramic Coaster', price: 7, hasVariants: true }
];

function AdminMuggs({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Batch upload state
  const [batchMode, setBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    images: [],
    video_url: null,
    product_type: 'mug',
    price: 18,
    has_variants: false,
    active: true,
    stock: null
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/admin/muggs-products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.images.length) {
      toast.error('Please add at least one photo');
      return;
    }
    const payload = { ...formData, image_url: formData.images[0] };
    
    try {
      if (editingProduct) {
        await axios.put(`/admin/muggs-products/${editingProduct.id}`, payload);
        toast.success('Product updated successfully');
      } else {
        await axios.post('/admin/muggs-products', payload);
        toast.success('Product added successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      images: [],
      video_url: null,
      product_type: 'mug',
      price: 18,
      has_variants: false,
      active: true,
      stock: null
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || '',
      images: product.images?.length ? product.images : [product.image_url].filter(Boolean),
      video_url: product.video_url || null,
      product_type: product.product_type,
      price: product.price,
      has_variants: product.has_variants || false,
      active: product.active !== false,
      stock: product.stock ?? null
    });
    setShowDialog(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`/admin/muggs-products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleProductTypeChange = (typeId) => {
    const typeInfo = PRODUCT_TYPES.find(t => t.id === typeId);
    setFormData({
      ...formData,
      product_type: typeId,
      price: typeInfo.price,
      has_variants: typeInfo.hasVariants || false
    });
  };

  const getProductTypeLabel = (typeId) => {
    const type = PRODUCT_TYPES.find(t => t.id === typeId);
    return type ? type.label : typeId;
  };

  const handleBatchFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setBatchUploading(true);
    const formDataUpload = new FormData();
    files.forEach(f => formDataUpload.append('files', f));

    try {
      const response = await axios.post('/upload/multiple', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // "pelican-shooter-left.jpg", "pelican-shooter-front.jpg" and
      // "pelican shooter.png" (the flat art) all become one product.
      const groups = new Map();
      response.data.uploaded.forEach(({ filename, url }) => {
        const { key } = splitViewName(filename);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ filename, url });
      });
      const newItems = [...groups.entries()].map(([key, files]) => ({
        _id: crypto.randomUUID(),
        urls: files.sort((x, y) => viewRank(x.filename) - viewRank(y.filename)).map((f) => f.url),
        title: filenameToTitle(key),
        product_type: 'mug'
      }));

      setBatchItems(prev => [...prev, ...newItems]);

      if (response.data.errors?.length) {
        toast.error(`${response.data.errors.length} file(s) failed to upload`);
      } else {
        toast.success(`${newItems.length} product(s) uploaded`);
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setBatchUploading(false);
      e.target.value = '';
    }
  };

  const updateBatchItem = (id, field, value) => {
    setBatchItems(prev => prev.map(item => item._id === id ? { ...item, [field]: value } : item));
  };

  const removeBatchItem = (id) => {
    setBatchItems(prev => prev.filter(item => item._id !== id));
  };

  const handleBatchSave = async () => {
    const untitled = batchItems.filter(item => !item.title.trim());
    if (untitled.length) {
      toast.error('Please add a title to every product before saving');
      return;
    }

    setBatchSaving(true);
    let saved = 0;
    let failed = 0;

    for (const item of batchItems) {
      const typeInfo = PRODUCT_TYPES.find(t => t.id === item.product_type);
      try {
        await axios.post('/admin/muggs-products', {
          title: item.title.trim(),
          description: '',
          image_url: item.urls[0],
          images: item.urls,
          product_type: item.product_type,
          price: typeInfo ? typeInfo.price : 18,
          has_variants: typeInfo ? (typeInfo.hasVariants || false) : false,
          active: false,
          stock: null
        });
        saved++;
      } catch {
        failed++;
      }
    }

    setBatchSaving(false);

    if (saved > 0) {
      toast.success(`${saved} product(s) saved as inactive — activate them from the grid`);
      setBatchItems([]);
      setBatchMode(false);
      fetchProducts();
    }
    if (failed > 0) {
      toast.error(`${failed} product(s) failed to save`);
    }
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="muggs">
      <div data-testid="muggs-admin-page">
        <div className="page-header">
          <h1>B.B. Muggs Products</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="outline"
              onClick={() => setBatchMode(m => !m)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Layers size={16} />
              {batchMode ? 'Cancel Batch' : 'Batch Upload'}
            </Button>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                {/* Photos + video */}
                <div className="form-group">
                  <label>Photos *</label>
                  <MuggPhotos
                    images={formData.images}
                    onChange={(images) => setFormData((f) => ({ ...f, images }))}
                    videoUrl={formData.video_url}
                    onVideoChange={(video_url) => setFormData((f) => ({ ...f, video_url }))}
                  />
                </div>

                {/* Title */}
                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label>Product Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., Sunrise Over the Dunes Mug"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  />
                </div>

                {/* Description */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the product"
                    rows={3}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  />
                </div>

                {/* Product Type */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Product Type *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => handleProductTypeChange(e.target.value)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  >
                    {PRODUCT_TYPES.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.label} — ${type.price}{type.hasVariants ? '+' : ''}
                      </option>
                    ))}
                  </select>
                  {formData.has_variants && (
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      * Coasters have quantity variants: Single ($7), Set of 2 ($12), Set of 4 ($21)
                    </p>
                  )}
                </div>

                {/* Stock */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Quantity in Stock</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock ?? ''}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                    placeholder="Leave blank for unlimited"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  />
                </div>

                {/* Active Toggle */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    Active (visible on shop page)
                  </label>
                </div>

                {/* Submit */}
                <div style={{ marginTop: '25px', display: 'flex', gap: '10px' }}>
                  <Button type="submit" style={{ flex: 1 }}>
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* ===== BATCH UPLOAD PANEL ===== */}
        {batchMode && (
          <div style={{ marginTop: '24px' }}>
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              border: '2px dashed #aaa', borderRadius: '12px', padding: '40px 20px',
              cursor: batchUploading ? 'not-allowed' : 'pointer',
              background: batchUploading ? '#f5f5f5' : '#fafafa',
              marginBottom: '24px'
            }}>
              <Upload size={36} color="#888" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#444' }}>
                {batchUploading ? 'Uploading…' : 'Click to select product images'}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#888' }}>
                Select multiple files at once. Files named design-left / -front / -back / -right plus the flat art (design.png) become one product
              </p>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleBatchFileSelect}
                disabled={batchUploading}
              />
            </label>

            {batchItems.length > 0 && (
              <>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  {batchItems.length} product{batchItems.length !== 1 ? 's' : ''} ready — fill in details then save. All will be saved as <strong>inactive</strong> until you activate them.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '20px',
                  marginBottom: '24px'
                }}>
                  {batchItems.map(item => (
                    <div key={item._id} style={{
                      background: '#fff', borderRadius: '12px', overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'relative'
                    }}>
                      <button
                        onClick={() => removeBatchItem(item._id)}
                        style={{
                          position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                          background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none',
                          borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <X size={14} />
                      </button>
                      <img src={item.urls[0]} alt={item.title} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} />
                      {item.urls.length > 1 && (
                        <div style={{ display: 'flex', gap: '4px', padding: '6px 14px 0' }}>
                          {item.urls.slice(1).map((u) => (
                            <img key={u} src={u} alt="" style={{ width: '36px', height: '36px', objectFit: 'contain', background: '#f3f3f3', borderRadius: '4px' }} />
                          ))}
                        </div>
                      )}
                      <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Title *</label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={e => updateBatchItem(item._id, 'title', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Product Type</label>
                          <select
                            value={item.product_type}
                            onChange={e => updateBatchItem(item._id, 'product_type', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                          >
                            {PRODUCT_TYPES.map(type => (
                              <option key={type.id} value={type.id}>{type.label} — ${type.price}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button variant="outline" onClick={() => { setBatchItems([]); setBatchMode(false); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleBatchSave} disabled={batchSaving} style={{ minWidth: '180px' }}>
                    {batchSaving ? 'Saving…' : `Save ${batchItems.length} Product${batchItems.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Products List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : products.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', background: '#f9f9f9', borderRadius: '12px' }}>
            <Coffee className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 style={{ margin: '0 0 10px' }}>No Products Yet</h3>
            <p style={{ color: '#666', margin: '0 0 20px' }}>Add your first B.B. Muggs product to get started.</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        ) : (
          <div className="products-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: '20px',
            marginTop: '20px'
          }}>
            {products.map(product => (
              <div 
                key={product.id} 
                className="product-card" 
                style={{ 
                  background: '#fff', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '12px',
                  overflow: 'hidden',
                  opacity: product.active ? 1 : 0.6
                }}
              >
                <div style={{ aspectRatio: '1', overflow: 'hidden', background: '#f5f5f5' }}>
                  <img 
                    src={product.image_url} 
                    alt={product.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ padding: '15px' }}>
                  <h3 style={{ fontSize: '16px', margin: '0 0 5px', fontWeight: '600' }}>{product.title}</h3>
                  <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px' }}>
                    {getProductTypeLabel(product.product_type)}
                  </p>
                  <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>
                    {(product.images?.length || 1)} photo{(product.images?.length || 1) !== 1 ? 's' : ''}
                    {product.video_url ? ' · video' : ''}
                  </p>
                  <p style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 5px' }}>
                    ${product.price.toFixed(2)}{product.has_variants ? '+' : ''}
                  </p>
                  <p style={{ fontSize: '13px', color: product.stock === 0 ? '#dc2626' : '#666', margin: '0 0 10px' }}>
                    {product.stock === null || product.stock === undefined ? 'Stock: Unlimited' : product.stock === 0 ? 'Out of Stock' : `Stock: ${product.stock}`}
                  </p>
                  {!product.active && (
                    <span style={{ 
                      background: '#fee2e2', 
                      color: '#dc2626', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      marginBottom: '10px',
                      display: 'inline-block'
                    }}>
                      Inactive
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEdit(product)}
                      style={{ flex: 1 }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      style={{ color: '#dc2626', borderColor: '#dc2626' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminMuggs;
